import type { ManualNameSpellings, NameLocalizations } from './localization';
import { authFetch } from "../utils/authFetch";
import { setCsrfToken } from "../csrf";

export interface AccountPreferences {
  push_enabled: boolean;
  language: "en" | "ar";
  text_size: "standard" | "large";
  density: "comfortable" | "compact";
  date_format: "dd/MM/yyyy" | "MM/dd/yyyy" | "yyyy-MM-dd";
  timezone: string;
}
export interface AccountProfile {
  name_localizations?: NameLocalizations;
  id: number;
  full_name: string;
  phone: string | null;
  email: string | null;
  role: string;
  department: string | null;
  branch: string | null;
  profile_image_path: string | null;
}
export interface AccountSettings {
  profile: AccountProfile;
  preferences: AccountPreferences;
  capabilities: {
    push_configured: boolean;
    email_available: boolean;
    can_edit: boolean;
  };
  csrf_token?: string;
}
const endpoint = "/employee/api/settings";
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await authFetch(endpoint + path, init);
  const data = await response.json();
  if (!response.ok) {
    const detail = data.detail;
    throw new Error(
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((item: { msg?: string }) => item.msg).join(". ")
          : "Unable to save account settings",
    );
  }
  if (data.csrf_token) setCsrfToken(data.csrf_token);
  return data as T;
}
function json(method: string, value: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(value),
  };
}
export const getAccountSettings = (): Promise<AccountSettings> => request("");
export const updateAccountPreferences = (
  patch: Partial<AccountPreferences>,
): Promise<{ preferences: AccountPreferences }> =>
  request("/preferences", json("PATCH", patch));
export const updateAccountProfile = (profile: ManualNameSpellings & {
  full_name: string;
  phone: string;
}): Promise<{ profile: AccountProfile }> =>
  request("/profile", json("PATCH", profile));
export const changeAccountPassword = (passwords: {
  current_password: string;
  new_password: string;
  confirm_password: string;
}): Promise<{ ok: boolean }> => request("/password", json("POST", passwords));
export function uploadAccountPhoto(
  image: File,
  profile: ManualNameSpellings & { full_name: string; phone: string },
): Promise<{ profile: AccountProfile }> {
  const body = new FormData();
  body.append("image", image);
  body.append("full_name", profile.full_name);
  body.append("phone", profile.phone);
  for (const field of ["name_ar", "name_en"] as const) {
    if (profile[field] !== undefined) body.append(field, profile[field]);
  }
  return request("/photo", { method: "POST", body });
}
export const testAccountPush = (
  endpoint: string,
): Promise<{ accepted: boolean }> =>
  request("/test-push", json("POST", { endpoint }));
